import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Textarea } from '../ui/Textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { 
  Brain, 
  Save, 
  X, 
  Info, 
  AlertCircle,
  Cpu,
  Target,
  Clock,
  Database,
  FileText
} from 'lucide-react';
import toast from 'react-hot-toast';

const AddModelModal = ({ isOpen, onClose, model, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'Classification',
    status: 'active',
    version: 'v1.0.0',
    trainingData: '',
    modelPath: '',
    framework: 'TensorFlow',
    accuracy: '',
    precision: '',
    recall: '',
    f1Score: '',
    inferenceTime: '',
    trainingTime: '',
    lastTrained: new Date().toISOString().split('T')[0],
    deploymentDate: '',
    tags: [],
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (model) {
        setFormData({
          name: model.name || '',
          description: model.description || '',
          type: model.type || 'Classification',
          status: model.status || 'active',
          version: model.version || 'v1.0.0',
          trainingData: model.trainingData || '',
          modelPath: model.modelPath || '',
          framework: model.framework || 'TensorFlow',
          accuracy: model.accuracy?.toString() || '',
          precision: model.performance?.precision?.toString() || '',
          recall: model.performance?.recall?.toString() || '',
          f1Score: model.performance?.f1Score?.toString() || '',
          inferenceTime: model.performance?.inferenceTime || '',
          trainingTime: model.trainingTime || '',
          lastTrained: model.lastTrained ? new Date(model.lastTrained).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          deploymentDate: model.deploymentDate ? new Date(model.deploymentDate).toISOString().split('T')[0] : '',
          tags: model.tags || [],
          notes: model.notes || ''
        });
      } else {
        resetForm();
      }
    }
  }, [isOpen, model]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'Classification',
      status: 'active',
      version: 'v1.0.0',
      trainingData: '',
      modelPath: '',
      framework: 'TensorFlow',
      accuracy: '',
      precision: '',
      recall: '',
      f1Score: '',
      inferenceTime: '',
      trainingTime: '',
      lastTrained: new Date().toISOString().split('T')[0],
      deploymentDate: '',
      tags: [],
      notes: ''
    });
    setErrors({});
    setNewTag('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Model name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.type) {
      newErrors.type = 'Model type is required';
    }

    if (formData.accuracy && (isNaN(formData.accuracy) || formData.accuracy < 0 || formData.accuracy > 100)) {
      newErrors.accuracy = 'Accuracy must be a number between 0 and 100';
    }

    if (formData.precision && (isNaN(formData.precision) || formData.precision < 0 || formData.precision > 100)) {
      newErrors.precision = 'Precision must be a number between 0 and 100';
    }

    if (formData.recall && (isNaN(formData.recall) || formData.recall < 0 || formData.recall > 100)) {
      newErrors.recall = 'Recall must be a number between 0 and 100';
    }

    if (formData.f1Score && (isNaN(formData.f1Score) || formData.f1Score < 0 || formData.f1Score > 100)) {
      newErrors.f1Score = 'F1 Score must be a number between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const modelData = {
        ...formData,
        accuracy: formData.accuracy ? parseFloat(formData.accuracy) : null,
        precision: formData.precision ? parseFloat(formData.precision) : null,
        recall: formData.recall ? parseFloat(formData.recall) : null,
        f1Score: formData.f1Score ? parseFloat(formData.f1Score) : null,
        performance: {
          precision: formData.precision ? parseFloat(formData.precision) : null,
          recall: formData.recall ? parseFloat(formData.recall) : null,
          f1Score: formData.f1Score ? parseFloat(formData.f1Score) : null,
          inferenceTime: formData.inferenceTime || null
        },
        lastTrained: formData.lastTrained ? new Date(formData.lastTrained).toISOString() : null,
        deploymentDate: formData.deploymentDate ? new Date(formData.deploymentDate).toISOString() : null
      };

      await onSave(modelData);
      toast.success(model ? 'Model updated successfully' : 'Model added successfully');
      resetForm();
      onClose();
    } catch (error) {
      toast.error(error.message || 'Failed to save model');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const modelTypes = [
    'Classification',
    'Sentiment Analysis',
    'Text Generation',
    'Translation',
    'Summarization',
    'Named Entity Recognition',
    'Question Answering',
    'Custom'
  ];

  const frameworks = [
    'TensorFlow',
    'PyTorch',
    'Scikit-learn',
    'Hugging Face',
    'OpenAI',
    'Custom'
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={model ? 'Edit AI Model' : 'Add New AI Model'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto">
        {/* Basic Information */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Brain className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Model Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="e.g., Ticket Classification Model"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={errors.name ? 'border-red-500' : ''}
                required
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="type">
                Model Type <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
                <SelectTrigger className={errors.type ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Select model type" />
                </SelectTrigger>
                <SelectContent>
                  {modelTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.type && (
                <p className="text-sm text-red-500">{errors.type}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Describe the purpose and functionality of this AI model..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className={errors.description ? 'border-red-500' : ''}
              required
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="version">Version</Label>
              <Input
                id="version"
                placeholder="v1.0.0"
                value={formData.version}
                onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="framework">Framework</Label>
              <Select value={formData.framework} onValueChange={(value) => setFormData(prev => ({ ...prev, framework: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select framework" />
                </SelectTrigger>
                <SelectContent>
                  {frameworks.map((framework) => (
                    <SelectItem key={framework} value={framework}>
                      {framework}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Metrics</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="accuracy">Accuracy (%)</Label>
              <Input
                id="accuracy"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="94.2"
                value={formData.accuracy}
                onChange={(e) => setFormData(prev => ({ ...prev, accuracy: e.target.value }))}
                className={errors.accuracy ? 'border-red-500' : ''}
              />
              {errors.accuracy && (
                <p className="text-sm text-red-500">{errors.accuracy}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="precision">Precision (%)</Label>
              <Input
                id="precision"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="94.2"
                value={formData.precision}
                onChange={(e) => setFormData(prev => ({ ...prev, precision: e.target.value }))}
                className={errors.precision ? 'border-red-500' : ''}
              />
              {errors.precision && (
                <p className="text-sm text-red-500">{errors.precision}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="recall">Recall (%)</Label>
              <Input
                id="recall"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="92.8"
                value={formData.recall}
                onChange={(e) => setFormData(prev => ({ ...prev, recall: e.target.value }))}
                className={errors.recall ? 'border-red-500' : ''}
              />
              {errors.recall && (
                <p className="text-sm text-red-500">{errors.recall}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="f1Score">F1 Score (%)</Label>
              <Input
                id="f1Score"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="93.5"
                value={formData.f1Score}
                onChange={(e) => setFormData(prev => ({ ...prev, f1Score: e.target.value }))}
                className={errors.f1Score ? 'border-red-500' : ''}
              />
              {errors.f1Score && (
                <p className="text-sm text-red-500">{errors.f1Score}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="inferenceTime">Inference Time</Label>
              <Input
                id="inferenceTime"
                placeholder="45ms"
                value={formData.inferenceTime}
                onChange={(e) => setFormData(prev => ({ ...prev, inferenceTime: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trainingTime">Training Time</Label>
              <Input
                id="trainingTime"
                placeholder="2h 30m"
                value={formData.trainingTime}
                onChange={(e) => setFormData(prev => ({ ...prev, trainingTime: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Training & Deployment */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <Clock className="h-5 w-5 text-green-600 dark:text-green-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Training & Deployment</h3>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="trainingData">Training Data</Label>
              <Input
                id="trainingData"
                placeholder="e.g., 12,000 samples"
                value={formData.trainingData}
                onChange={(e) => setFormData(prev => ({ ...prev, trainingData: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="modelPath">Model Path/URL</Label>
              <Input
                id="modelPath"
                placeholder="/models/ticket-classification"
                value={formData.modelPath}
                onChange={(e) => setFormData(prev => ({ ...prev, modelPath: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastTrained">Last Trained</Label>
              <Input
                id="lastTrained"
                type="date"
                value={formData.lastTrained}
                onChange={(e) => setFormData(prev => ({ ...prev, lastTrained: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="deploymentDate">Deployment Date</Label>
              <Input
                id="deploymentDate"
                type="date"
                value={formData.deploymentDate}
                onChange={(e) => setFormData(prev => ({ ...prev, deploymentDate: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tags & Notes</h3>
          </div>

          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex gap-2 flex-wrap mb-2">
              {formData.tags.map((tag, index) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-red-500"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add tag..."
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button type="button" onClick={handleAddTag} variant="outline">
                Add
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              placeholder="Additional notes about the model..."
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} loading={loading}>
            <Save className="h-4 w-4" />
            {model ? 'Update Model' : 'Create Model'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddModelModal;

